import '../../MMM/ModelResults/ChannelPlatformTacticBrand.scss';
import * as Dfns from 'date-fns/fp';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState, Dispatch, SetStateAction, FC } from 'react';
import { formatMoney } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { CptbData } from '@blisspointmedia/bpm-types/dist/MMM';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Dropdown } from '@Components/Dropdown';
import { DropdownToggleType } from '@Components/Dropdown/dropdown-constants';
import { InfoTooltip } from '@Components/InfoTooltip';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { ChartContainer } from '@Components/ChartContainer';
import { AreaChart } from '@Components/Charts/AreaChart';
import { LineChart } from '@Components/Charts/LineChart';
import { DatePicker } from '@Components/DatePicker';
import { Design, Placement, Size, StretchMode } from '@Components/designConstants';
import { SnapshotChart } from '@Components/SnapshotChart/SnapshotChart';
import { WidgetContainer } from '@Components/WidgetContainer';
import {
  capitalizeWords,
  cptbAreaData,
  currencyFormatter,
  snapShotDataSort
} from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { CONVERSION_FORMATTER_TYPE } from '@Pages/MMM/mmm-utils';
import {
  calculateTTMOverallRoasCpa,
  cptbSnapShotData,
  cptbTwoStageSnapData,
  StageTwoFileData
} from '@Components/Charts/ModelResults/DataFormatters';
import { dateToMddyy } from '@src/utils/date-utils';
import { downloadPNG, exportToExcel } from '@src/utils/download-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { Neutral400 } from '@src/utils/colors';
import { MIN_PERIOD_SPEND_FOR_ROAS } from '../utils/roasPipeline';
import { formatPercentRounded, toIsoDate, TTM_WEEKS } from '../utils/hroasUtils';
import { StackedAreaChart } from '../../MMM/ModelResults/StackedAreaChart';
import { TwoStageSpendandEffectContainer } from '../../MMM/ModelResults/TwoStageSpendAndEffectContainer';
import IncrementalityIcon from './Icons/IncrementalityIcon.svg?react';

// Re-exported for existing consumers/tests that import it from this file (DEV-21662) -- the
// implementation now lives in hroasUtils.ts, shared with the TTM % Credit to Paid Media fix.
export { toIsoDate };

interface AreaChartData {
  areas: any;
  newD: any[];
  groupedAreas: any;
}

const CONVERSION_TYPE_STRING = {
  conversion: 'KPI Volume',
  revenue: 'Revenue'
};

const DEFAULT_SORT_SELECTIONS = {
  spendAndEffectShare: 'Spend: High to low',
  revenue: 'Highest to lowest',
  costPerAcquisition: 'Highest to lowest',
  roas: 'Highest to lowest'
};

const DEFAULT_SORT_OPTIONS = [
  { value: 'Alphabetical' },
  { value: 'Highest to lowest' },
  { value: 'Lowest to highest' }
];

export type DateGroupingKey = 'Day' | 'Week' | 'Month' | 'Quarter' | 'Year';
export interface DateGroupingOption {
  value: DateGroupingKey;
  label: string;
}
export const DATE_GROUPING_OPTIONS: DateGroupingOption[] = [
  { value: 'Week', label: 'Weekly' },
  { value: 'Month', label: 'Monthly' },
  { value: 'Quarter', label: 'Quarterly' }
];

interface ChannelPlatformTacticBrandProps {
  cptbData: CptbData[];
  commonDateRange?: { start: string; end: string } | null;
  disabledPlatform: boolean | undefined;
  groupBy: string;
  setGroupBy: (groupBy: string) => void;
  groupByOptions: { label: string; value: string }[];
  kpiType: string;
  setOverallRoasCpa: Dispatch<SetStateAction<number | undefined>>;
  showActualizedData: boolean;
  twoStageData?: StageTwoFileData[];
  kpi: string;
  modelDate: string;
  // Whether a Brand Equity slot is enabled, for the covariate residual (DEV-21662).
  isBrandEquityEnabled?: boolean;
  // Brand Equity's per-date predicted revenue (date -> dollars), used by the covariate residual
  // when BE is enabled. See ModelResults.tsx's beePredictedRevenueByDate for how this is built.
  beePredictedRevenueByDate?: Map<string, number>;
  // Weighted paid_attribution across enabled models (date -> dollars) -- the same numerator TTM
  // % Credit to Paid Media uses. See ModelResults.tsx's paidAttributionByDate.
  paidAttributionByDate?: Map<string, number>;
}

const HARD_CODED_COLOR = Neutral400;
const HARD_CODED_COLOR_NAME = 'Covariates';

/**
 * Finds the actual min and max dates from the data array.
 */
export const getDefaultDates = <T extends { date: string }>(
  data: T[]
): {
  start: string;
  end: string;
} => {
  if (!data || data.length === 0) {
    return { start: '', end: '' };
  }

  const dates = data.map(item => item.date).filter(Boolean);
  if (dates.length === 0) {
    return { start: '', end: '' };
  }

  const sortedDates = [...dates].sort();
  const start = sortedDates[0];
  const end = sortedDates[sortedDates.length - 1];

  return { start, end };
};

/**
 * Clamps a {start, end} range's start to the trailing TTM_WEEKS back from `end` (matching the Key
 * Results card's TTM window), never going earlier than the range's own true start. Shared by both
 * places in this component that seed the date picker's default selection (`dates` starts empty
 * and gets set by whichever of two independent effects resolves its data first -- see the
 * twoStageData- and cptbData-driven effects below), so the TTM default applies consistently
 * regardless of which one wins that race.
 */
const clampToTtmWindow = (range: {
  start: string;
  end: string;
}): { start: string; end: string } => {
  if (!range.end) {
    return range;
  }

  const ttmStart = new Date(range.end);
  ttmStart.setDate(ttmStart.getDate() - TTM_WEEKS * 7);
  const ttmStartStr = ttmStart.toISOString().slice(0, 10);

  return { start: ttmStartStr > range.start ? ttmStartStr : range.start, end: range.end };
};

/**
 * Same as getDefaultDates, but the default start is clamped to the trailing TTM_WEEKS back from
 * the data's max date instead of the data's true min date. The date picker's own selectable
 * bounds still span the full history -- only the initial selection defaults to TTM.
 */
export const getDefaultTtmDates = <T extends { date: string }>(
  data: T[]
): {
  start: string;
  end: string;
} => clampToTtmWindow(getDefaultDates(data));

/**
 * Filter data by date range.
 */
const filterDataByDateRange = <T extends { date: string }>(
  data: T[],
  startDate: string,
  endDate: string
): T[] =>
  data.filter(item => {
    const itemDate = new Date(item.date);
    return itemDate >= new Date(startDate) && itemDate <= new Date(endDate);
  });

// Sums a date -> dollars map over [startDate, endDate], reusing filterDataByDateRange's boundary
// semantics. Used for the covariate residual's BE-predicted-revenue and paid-attribution totals.
// Converts dates to ISO via toIsoDate first -- see its doc comment in hroasUtils.ts for why.
export const sumDateMapInRange = (
  dateMap: Map<string, number>,
  startDate: string,
  endDate: string
): number =>
  filterDataByDateRange(
    Array.from(dateMap, ([date, value]) => ({ date: toIsoDate(date), value })),
    startDate,
    endDate
  ).reduce((sum, row) => sum + row.value, 0);

const WIDGET_SUBHEADER = {
  revenue: (
    <>
      Attribution of revenue to each investment based on model parameter estimates.
      <InfoTooltip rightLabel='More info' size={Size.SM}>
        Calculated for each point in time as (level of media investment at that point in time) x
        (model parameter estimates for that media input).
      </InfoTooltip>
    </>
  ),
  costPerAcquisition: (
    <>
      Attribution of cost-pers to each investment based on model parameter estimates.
      <InfoTooltip rightLabel='More info' size={Size.SM}>
        Calculated for each point in time as (level of media investment at that point in time) /
        ((level of media investment at that point in time) x (model parameter estimates for that
        media input)).
      </InfoTooltip>
    </>
  ),
  roas: (
    <>
      Attribution of ROAS to each investment based on model parameter estimates.
      <InfoTooltip rightLabel='More info' size={Size.SM}>
        Calculated for each point in time as ((level of media investment at that point in time) x
        (model parameter estimates for that media input)) / (level of media investment at that point
        in time).
      </InfoTooltip>
    </>
  ),
  spendAndEffectShare: (
    <>
      Attribution of spend and effect share to each investment based on model parameter estimates.
      <InfoTooltip rightLabel='More info' size={Size.SM}>
        Effect share calculated for each point in time as ((level of media investment at that point
        in time) x (model parameter estimates for that media input)) / (total KPI volume).
      </InfoTooltip>
    </>
  )
};

export const ChannelPlatformTacticBrand: FC<ChannelPlatformTacticBrandProps> = ({
  cptbData,
  commonDateRange,
  disabledPlatform,
  groupBy,
  setGroupBy,
  groupByOptions,
  kpiType,
  setOverallRoasCpa,
  showActualizedData,
  twoStageData,
  kpi,
  modelDate,
  isBrandEquityEnabled = false,
  beePredictedRevenueByDate,
  paidAttributionByDate
}) => {
  const [investment, setInvestment] = useState('spendAndEffectShare');
  const [dateGrouping, setDateGrouping] = useState('Week');
  const [dates, setDates] = useState({ start: '', end: '' });
  const [maxCptbDate, setMaxCptbDate] = useState<Date>();
  const [covariates, setCovariates] = useState(false);
  const [areaChartData, setAreaChartData] = useState<AreaChartData>({
    areas: [],
    newD: [],
    groupedAreas: []
  });
  const [areaChart2Data, setAreaChart2Data] = useState<AreaChartData>({
    areas: [],
    newD: [],
    groupedAreas: []
  });
  const [toggleLabel, setToggleLabel] = useState(false);
  const [snapChartSortValue, setSnapChartSortValue] = useState('Spend: High to low');
  const [absoluteOrRelative, setAbsoluteOrRelative] = useState('Absolute');
  const [twoStageBarData, setTwoStageBarData] = useState<any[]>([]);

  // Media-Driven Brand Equity is hidden from the collapsed Spend and Effect Share list -- it's
  // fully explained by demand-gen tactics and not a channel an analyst allocates spend against
  // (DEV-21662) -- but stays visible in the "Dynamics" modal, where its role as a contributor to
  // other real channels' own totals is meaningful. The modal gets the full, unfiltered
  // `twoStageBarData` via TwoStageSpendandEffectContainer's `modalData` prop below.
  const twoStageBarDataWithoutMdbe = useMemo(
    () => twoStageBarData.filter(item => item.name?.toLowerCase() !== 'media-driven brand equity'),
    [twoStageBarData]
  );

  const { cid: company } = useCompanyInfo();
  const companyEqualsTest = TEST_COMPANIES.includes(company);

  const SNAPCHART_TITLE = {
    spendAndEffectShare: 'Spend and Effect Share',
    revenue: `${CONVERSION_TYPE_STRING[kpiType]} Decomposition Share`,
    costPerAcquisition: 'Average CPA',
    roas: 'Average ROAS'
  };

  const INVESTMENT_OPTIONS = useMemo(
    () => [
      { label: 'Spend and Effect Share', value: 'spendAndEffectShare', title: 'Spend and Effect' },
      {
        label: CONVERSION_TYPE_STRING[kpiType],
        value: 'revenue',
        title: `${CONVERSION_TYPE_STRING[kpiType]} Decomposition`
      },
      {
        label: 'Cost Per Acquisition',
        value: 'costPerAcquisition',
        title: 'Cost Per Acquisition'
      },
      { label: 'ROAS', value: 'roas', title: 'ROAS' }
    ],
    [kpiType]
  );

  const SPEND_AND_EFFECT_SORT_OPTIONS = [
    { value: 'Alphabetical' },
    { value: 'Spend to effect ratio' },
    { value: 'Spend: High to low' },
    { value: 'Spend: Low to high' },
    { value: `${CONVERSION_TYPE_STRING[kpiType]}: High to low` },
    { value: `${CONVERSION_TYPE_STRING[kpiType]}: Low to high` }
  ] as const;

  useEffect(() => {
    if (twoStageData) {
      if (dates.start === '' || dates.end === '') {
        const defaultDates = getDefaultTtmDates(twoStageData);
        if (defaultDates.start !== '' && defaultDates.end !== '') {
          setDates(defaultDates);
        }
        return;
      }

      if (dates.start.length && dates.end.length) {
        const dateFilteredData = filterDataByDateRange(twoStageData, dates.start, dates.end);

        // Sum BE's predicted revenue and the weighted paid-attribution total over the same
        // selected window as dateFilteredData, so the covariate residual (DEV-21662) stays
        // date-consistent with everything else in this view and matches TTM % Credit to Paid
        // Media's own methodology (same paid_attribution numerator, BE's predicted revenue
        // denominator).
        const beePredictedRevenueTotal =
          isBrandEquityEnabled && beePredictedRevenueByDate
            ? sumDateMapInRange(beePredictedRevenueByDate, dates.start, dates.end)
            : 0;
        const paidAttributionTotal =
          isBrandEquityEnabled && paidAttributionByDate
            ? sumDateMapInRange(paidAttributionByDate, dates.start, dates.end)
            : 0;

        const formattedData = cptbTwoStageSnapData(
          dateFilteredData,
          disabledPlatform,
          CONVERSION_TYPE_STRING[kpiType],
          kpiType,
          covariates,
          groupBy,
          isBrandEquityEnabled,
          beePredictedRevenueTotal,
          paidAttributionTotal
        );
        setTwoStageBarData(snapShotDataSort(formattedData.barData, snapChartSortValue));
      }
    }
  }, [
    twoStageData,
    isBrandEquityEnabled,
    beePredictedRevenueByDate,
    paidAttributionByDate,
    disabledPlatform,
    kpiType,
    snapChartSortValue,
    showActualizedData,
    dates,
    covariates,
    groupBy
  ]);

  useEffect(() => {
    if (showActualizedData) {
      setTwoStageBarData(d => snapShotDataSort(d || [], snapChartSortValue));
    }
  }, [snapChartSortValue, showActualizedData, groupBy]);

  useEffect(() => {
    if (!R.isEmpty(cptbData)) {
      const dateRange = getDefaultDates(cptbData);
      setMaxCptbDate(Dfns.parseISO(dateRange.end));
    }
  }, [cptbData, setMaxCptbDate]);

  // Memoize filtered data by date range
  const dateRangeFilteredDataMemo = useMemo(() => {
    if (R.isEmpty(cptbData) || dates.start === '' || dates.end === '') {
      return [];
    }
    const filtered = filterDataByDateRange(cptbData, dates.start, dates.end);
    return covariates ? filtered : filtered.filter(item => item.name !== 'Covariates');
  }, [cptbData, dates.start, dates.end, covariates]);

  // Memoize area chart data based on investment type
  const areaChartDataMemo = useMemo(() => {
    if (R.isEmpty(dateRangeFilteredDataMemo)) {
      return {
        areas: [],
        newD: [],
        groupedAreas: []
      };
    }

    let result;
    switch (investment) {
      case 'revenue':
        result = cptbAreaData({
          fiscalCal: {},
          data: dateRangeFilteredDataMemo,
          data1Key: 'adjusted_outcome_to_observed',
          groupBy,
          dateGrouping,
          disabledPlatform,
          relative: absoluteOrRelative === 'Relative',
          companyEqualsTest
        });
        break;
      case 'costPerAcquisition':
        result = cptbAreaData({
          fiscalCal: {},
          data: dateRangeFilteredDataMemo,
          data1Key: 'raw_spend',
          groupBy,
          dateGrouping,
          data2Key: 'predicted_outcome',
          disabledPlatform,
          companyEqualsTest
        });
        break;
      case 'roas':
        result = cptbAreaData({
          fiscalCal: {},
          data: dateRangeFilteredDataMemo,
          data1Key: 'predicted_outcome',
          groupBy,
          dateGrouping,
          data2Key: 'raw_spend',
          disabledPlatform,
          companyEqualsTest,
          // Defer the divide until after period roll-up so weekly/monthly/quarterly
          // ROAS = sum_period(effect) / sum_period(aligned_spend) rather than
          // sum_period(daily_roas). Periods with spend below the cutoff are zeroed
          // to suppress lag-induced outliers (D3, D4).
          roasCutoffMin: MIN_PERIOD_SPEND_FOR_ROAS
        });
        break;
      default: {
        result = cptbAreaData({
          fiscalCal: {},
          data: dateRangeFilteredDataMemo,
          data1Key: 'raw_spend',
          groupBy,
          dateGrouping,
          disabledPlatform,
          relative: absoluteOrRelative === 'Relative',
          companyEqualsTest
        });
        break;
      }
    }
    return result;
  }, [
    dateRangeFilteredDataMemo,
    investment,
    groupBy,
    dateGrouping,
    disabledPlatform,
    absoluteOrRelative,
    companyEqualsTest
  ]);

  // Memoize area chart 2 data (only for default case)
  const areaChart2DataMemo = useMemo(() => {
    if (R.isEmpty(dateRangeFilteredDataMemo) || investment !== 'spendAndEffectShare') {
      return {
        areas: [],
        newD: [],
        groupedAreas: []
      };
    }
    return cptbAreaData({
      fiscalCal: {},
      data: dateRangeFilteredDataMemo,
      data1Key: 'adjusted_outcome_to_observed',
      groupBy,
      dateGrouping,
      disabledPlatform,
      relative: absoluteOrRelative === 'Relative',
      companyEqualsTest
    });
  }, [
    dateRangeFilteredDataMemo,
    investment,
    groupBy,
    dateGrouping,
    disabledPlatform,
    absoluteOrRelative,
    companyEqualsTest
  ]);

  // Memoize snapshot chart data
  const snapChartDataMemo = useMemo(() => {
    if (R.isEmpty(dateRangeFilteredDataMemo)) {
      return { barData: [], titleData: undefined };
    }
    const snapData = cptbSnapShotData({
      data: dateRangeFilteredDataMemo,
      groupBy,
      type: investment,
      disabledPlatform,
      companyEqualsTest,
      conversionPrettyName: CONVERSION_TYPE_STRING[kpiType],
      kpiType
    });
    return {
      ...snapData,
      barData: snapShotDataSort(snapData.barData, snapChartSortValue)
    };
  }, [
    dateRangeFilteredDataMemo,
    groupBy,
    investment,
    disabledPlatform,
    companyEqualsTest,
    kpiType,
    snapChartSortValue
  ]);

  const exportFormattedData = useMemo(() => {
    if (!showActualizedData && cptbData && !R.isEmpty(snapChartDataMemo.barData)) {
      const refreshDate = cptbData.length ? cptbData[0].refresh_date || '' : '';

      return snapChartDataMemo.barData.map(obj => {
        const sharedValues = {
          company,
          kpi,
          refresh_date: refreshDate,
          media_name: obj.name
        };
        switch (investment) {
          case 'revenue':
            return {
              ...sharedValues,
              decomposition_percent: obj.value,
              decomposition_value: obj.labelRawNumber
            };
          case 'costPerAcquisition':
            return {
              ...sharedValues,
              average_cpa: obj.value,
              efficiency_ratio_to_overall_cpa: obj.labelRawNumber
            };
          case 'roas':
            return {
              ...sharedValues,
              average_roas: obj.value,
              efficiency_ratio_to_overall_roas: obj.labelRawNumber
            };
          default:
            return {
              ...sharedValues,
              spend_share_percent: obj.value,
              effect_share_percent: obj.value2,
              spend_volume: obj.spend_volume,
              effect_volume: obj.effect_volume
            };
        }
      });
    }
    if (showActualizedData && twoStageData && twoStageBarData) {
      const refreshDate = twoStageData.length ? twoStageData[0].refresh_date || '' : '';

      return twoStageBarData.map(obj => ({
        company,
        kpi,
        refresh_date: refreshDate,
        media_name: obj.name,
        spend_actualized_percent: obj.totalActualizedSpend,
        effect_actualized_percent: obj.totalActualizedEffect,
        spend_actualized_volume: obj.totalActualizedSpendRawValue,
        effect_actualized_volume: obj.totalActualizedEffectRawValue
      }));
    }
    return [];
  }, [
    showActualizedData,
    snapChartDataMemo,
    twoStageBarData,
    company,
    kpi,
    cptbData,
    twoStageData,
    investment
  ]);

  // Memoize TTM filtered data
  const ttmFilteredDataMemo = useMemo(() => {
    if (!maxCptbDate || R.isEmpty(cptbData)) {
      return [];
    }
    const TTMsDate = Dfns.subYears(1)(maxCptbDate);
    const ttmStartDate = Dfns.format('yyyy-MM-dd')(TTMsDate);
    const ttmEndDate = Dfns.format('yyyy-MM-dd')(maxCptbDate);

    const ttmFiltered = filterDataByDateRange(cptbData, ttmStartDate, ttmEndDate);
    return covariates ? ttmFiltered : ttmFiltered.filter(item => item.name !== 'Covariates');
  }, [cptbData, maxCptbDate, covariates]);

  // Memoize TTM overall ROAS/CPA calculation
  const ttmOverallRoasCpaMemo = useMemo(() => {
    if (R.isEmpty(ttmFilteredDataMemo)) {
      return undefined;
    }
    return calculateTTMOverallRoasCpa(ttmFilteredDataMemo, kpiType);
  }, [ttmFilteredDataMemo, kpiType]);

  useEffect(() => {
    if (R.isEmpty(cptbData)) {
      return;
    }
    const fullRange = commonDateRange ?? getDefaultDates(cptbData);
    setDates(prev => {
      if (!prev.start || !prev.end) {
        // Whichever of this effect and the twoStageData-driven one above resolves its data
        // first wins the "seed the empty default" race -- both must default to the TTM window
        // for the result to be consistent regardless of load order.
        return clampToTtmWindow(fullRange);
      }
      const stillInRange =
        Dfns.parseISO(prev.start) >= Dfns.parseISO(fullRange.start) &&
        Dfns.parseISO(prev.end) <= Dfns.parseISO(fullRange.end);
      return stillInRange ? prev : fullRange;
    });
  }, [commonDateRange, cptbData]);

  // Side effect: Set overall ROAS/CPA
  useEffect(() => {
    if (ttmOverallRoasCpaMemo !== undefined) {
      setOverallRoasCpa(ttmOverallRoasCpaMemo);
    }
  }, [ttmOverallRoasCpaMemo, setOverallRoasCpa]);

  // Side effect: Update chart data state from memoized values
  useEffect(() => {
    if (!R.isEmpty(dateRangeFilteredDataMemo) && maxCptbDate) {
      setAreaChartData(areaChartDataMemo);
      if (investment === 'spendAndEffectShare') {
        setAreaChart2Data(areaChart2DataMemo);
      }
    }
  }, [dateRangeFilteredDataMemo, maxCptbDate, areaChartDataMemo, areaChart2DataMemo, investment]);

  useEffect(() => {
    setInvestment('spendAndEffectShare');
    setSnapChartSortValue('Spend: High to low');
  }, [kpiType]);

  const buildInvestmentOptions = () =>
    INVESTMENT_OPTIONS.filter(
      item =>
        !(kpiType === 'conversion' && item.value === 'roas') &&
        !(kpiType === 'revenue' && item.value === 'costPerAcquisition')
    );

  const revenueAbsoluteRelativeFormatter = useCallback(
    val => {
      if (absoluteOrRelative === 'Absolute') {
        if (kpiType === 'conversion') {
          return CONVERSION_FORMATTER_TYPE[kpiType].format(val, 0);
        }
        return val < 1000
          ? CONVERSION_FORMATTER_TYPE[kpiType].format(val, 0)
          : CONVERSION_FORMATTER_TYPE[kpiType].format(val, 1);
      }
      return absoluteOrRelative === 'Relative' ? formatPercentRounded(val) : '';
    },
    [absoluteOrRelative, kpiType]
  );
  const spendAbsoluteRelativeFormatter = useCallback(
    val => {
      if (absoluteOrRelative === 'Absolute') {
        return val < 1000 ? currencyFormatter.format(val, 0) : currencyFormatter.format(val, 1);
      }
      return absoluteOrRelative === 'Relative' ? formatPercentRounded(val) : '';
    },
    [absoluteOrRelative]
  );

  const xAxisTickFormatter = useCallback((val: string) => dateToMddyy(val), []);

  const buildRightChart = () => {
    switch (investment) {
      case 'revenue':
        return (
          <AreaChart
            areas={areaChartData.areas}
            data={areaChartData.newD}
            dateGrouping={dateGrouping}
            hardCodedColorName={HARD_CODED_COLOR_NAME}
            hardCodedColor={HARD_CODED_COLOR}
            incrementality
            groupedAreas={areaChartData.groupedAreas}
            tooltipFormatter={val => revenueAbsoluteRelativeFormatter(val)}
            nonNegativeYAxis
            usePercentageYAxis={absoluteOrRelative === 'Relative'}
            xAxisDataKey='date'
            xAxisTickFormatter={xAxisTickFormatter}
            yAxisTickFormatter={val => `${CONVERSION_FORMATTER_TYPE[kpiType].format(val, 0)}`}
          />
        );
      case 'costPerAcquisition':
        return (
          <LineChart
            data={areaChartData?.newD}
            dateGrouping={dateGrouping}
            colorMappings={[{ name: HARD_CODED_COLOR_NAME, color: HARD_CODED_COLOR }]}
            groupedAreas={areaChartData.groupedAreas}
            lineDataKeys={areaChartData?.areas}
            tooltipLabelFormatter={val => (val ? capitalizeWords(val) : val)}
            tooltipFormatter={val => {
              if (!val) {
                return val;
              }

              return val > 15 ? currencyFormatter.format(val, 0) : currencyFormatter.format(val, 2);
            }}
            useLeftYAxis
            xAxisDataKey='date'
            xAxisTickFormatter={xAxisTickFormatter}
            yAxisTickFormatter={val => currencyFormatter.format(val, 0)}
          />
        );
      case 'roas':
        return (
          <LineChart
            data={areaChartData?.newD}
            dateGrouping={dateGrouping}
            colorMappings={[{ name: HARD_CODED_COLOR_NAME, color: HARD_CODED_COLOR }]}
            groupedAreas={areaChartData.groupedAreas}
            lineDataKeys={areaChartData?.areas}
            tooltipLabelFormatter={val => (val ? capitalizeWords(val) : val)}
            tooltipFormatter={val => (val ? `${val.toFixed(2)}` : val)}
            tooltipShape='line'
            useLeftYAxis
            xAxisDataKey='date'
            xAxisTickFormatter={xAxisTickFormatter}
          />
        );
      default:
        return (
          <div className='cptb-stacked-chart-wrapper'>
            <StackedAreaChart
              areas={areaChartData.areas}
              chart1Title='Spend'
              chart2Title={`Effect (${CONVERSION_TYPE_STRING[kpiType]})`}
              chart2NonNegativeYAxis
              data1={areaChartData.newD}
              data2={areaChart2Data.newD}
              dateGrouping={dateGrouping}
              groupedAreas={areaChartData.groupedAreas}
              hardCodedColorName={HARD_CODED_COLOR_NAME}
              hardCodedColor={HARD_CODED_COLOR}
              xAxisDataKey='date'
              tooltipFormatterTop={val => spendAbsoluteRelativeFormatter(val)}
              tooltipFormatterBottom={val => revenueAbsoluteRelativeFormatter(val)}
              usePercentageYAxis={absoluteOrRelative === 'Relative'}
              xAxisTickFormatter={xAxisTickFormatter}
              yAxisTickFormatter1={val => currencyFormatter.format(val, 0)}
              yAxisTickFormatter2={val => `${CONVERSION_FORMATTER_TYPE[kpiType].format(val, 0)}`}
            />
          </div>
        );
    }
  };

  const rightChartTitle = useCallback(() => {
    const option = INVESTMENT_OPTIONS.find(item => item.value === investment);
    return option?.title;
  }, [investment, INVESTMENT_OPTIONS]);

  const snapChartFormatter = useCallback(
    value => {
      switch (investment) {
        case 'costPerAcquisition':
          return value === null ? '--' : formatMoney(value);
        case 'roas':
          return value === null ? '--' : `${value.toFixed(2)}`;
        case 'revenue':
        default:
          return value === null ? '--' : formatPercentRounded(value);
      }
    },
    [investment]
  );

  const buildSnapshotHeader = useCallback(() => {
    switch (investment) {
      case 'costPerAcquisition':
      case 'roas':
        return `Overall ${investment === 'roas' ? 'ROAS' : 'CPA'} ${
          snapChartDataMemo.titleData || ''
        }`;
      default:
        return undefined;
    }
  }, [investment, snapChartDataMemo]);

  const excelDownloadCPTB = useCallback(() => {
    exportToExcel(cptbData, 'CPTB_Data');
  }, [cptbData]);

  const pngDownloadRight = useCallback(async () => {
    await downloadPNG('.cptb .right .chart-container', `${rightChartTitle()}`);
  }, [rightChartTitle]);

  return (
    <WidgetContainer
      footer={
        <div className='mmm-widget-bottom-wrapper'>
          <IncrementalityIcon className='incrementality-icon-bottom' />
          <div>
            Channels marked with a green beaker use informative priors based on incrementality
            studies. Incrementality testing is strongly recommended, and we encourage you to contact
            your team to review options.
          </div>
        </div>
      }
      collapsible
      header={
        <div className='cptb-widget-dropdown-wrapper'>
          <span>How well did our investments do based on </span>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            onChange={option => {
              setSnapChartSortValue(DEFAULT_SORT_SELECTIONS[option]);
              setInvestment(option);
            }}
            options={buildInvestmentOptions()}
            type={DropdownToggleType.WIDGET_TITLE}
            value={investment}
          />
          <span>?</span>
        </div>
      }
      rightActions={
        <>
          <div className='cptb-covariates-toggle'>
            <ToggleSwitch
              design={Design.BRAND}
              size={Size.LG}
              label='Covariates'
              checked={covariates}
              onChange={() => setCovariates(!covariates)}
              infoTooltipText='An independent variable that can influence the outcome of a given statistical trial, but which is not of direct interest.'
            />
          </div>
          <DatePicker
            key={`datepicker-${dates.start}-${dates.end}`}
            range={dates}
            isOutsideRange={date => {
              const range = commonDateRange ?? getDefaultDates(cptbData);
              return new Date(date) < new Date(range.start) || new Date(date) > new Date(range.end);
            }}
            onChange={({ start, end }) => {
              setDates({
                start,
                end
              });
            }}
          />
        </>
      }
      subHeader={WIDGET_SUBHEADER[investment]}
    >
      <div className='cptb'>
        <div className='left'>
          {showActualizedData && investment === 'spendAndEffectShare'
            ? twoStageBarData && (
                <TwoStageSpendandEffectContainer
                  colorOverrides={{ Covariates: Neutral400 }}
                  twoStageData={twoStageBarDataWithoutMdbe}
                  modalData={twoStageBarData}
                  dropdownOptions={SPEND_AND_EFFECT_SORT_OPTIONS}
                  dropdownValue={snapChartSortValue}
                  setDropdownValue={setSnapChartSortValue}
                  title={SNAPCHART_TITLE[investment]}
                  valueFormatter={snapChartFormatter}
                  exportFormattedData={exportFormattedData}
                  kpiType={kpiType}
                  groupBy={groupBy}
                  setGroupBy={setGroupBy}
                  groupByOptions={groupByOptions}
                  sortDropdownValue={snapChartSortValue}
                  setSortDropdownValue={setSnapChartSortValue}
                  modelDate={modelDate}
                />
              )
            : !R.isEmpty(snapChartDataMemo.barData) && (
                <SnapshotChart
                  colorOverrides={{ Covariates: Neutral400 }}
                  data={snapChartDataMemo.barData}
                  dropdownOptions={
                    investment === 'spendAndEffectShare'
                      ? SPEND_AND_EFFECT_SORT_OPTIONS
                      : DEFAULT_SORT_OPTIONS
                  }
                  dropdownValue={snapChartSortValue}
                  setDropdownValue={setSnapChartSortValue}
                  doubleBarChart={investment === 'spendAndEffectShare'}
                  header={buildSnapshotHeader()}
                  setToggleLabel={
                    investment === 'revenue' || investment === 'spendAndEffectShare'
                      ? setToggleLabel
                      : undefined
                  }
                  title={SNAPCHART_TITLE[investment]}
                  toggleLabel={
                    investment === 'revenue' || investment === 'spendAndEffectShare'
                      ? toggleLabel
                      : true
                  }
                  toggleSwitchLabel={kpiType === 'conversion' ? 'Volume' : '$'}
                  valueFormatter={snapChartFormatter}
                  exportFormattedData={exportFormattedData}
                />
              )}
        </div>
        <div className='right'>
          <ChartContainer
            title={
              <>
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  size={Size.LG}
                  className='chart-title-dropdown'
                  type={DropdownToggleType.WIDGET_TITLE}
                  value={dateGrouping}
                  options={DATE_GROUPING_OPTIONS}
                  onChange={option => setDateGrouping(option)}
                />
                <div>{rightChartTitle()}</div>
              </>
            }
            rightActions={
              <>
                {(investment === 'revenue' || investment === 'spendAndEffectShare') && (
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    size={Size.SM}
                    stretch={StretchMode.FIT}
                    value={absoluteOrRelative}
                    options={[{ value: 'Absolute' }, { value: 'Relative' }]}
                    onChange={option => setAbsoluteOrRelative(option)}
                  />
                )}
                <DownloadDropdown
                  size={Size.SM}
                  onClickOptions={[excelDownloadCPTB, pngDownloadRight]}
                />
              </>
            }
          >
            {!R.isEmpty(areaChartData) && buildRightChart()}
          </ChartContainer>
        </div>
      </div>
    </WidgetContainer>
  );
};
